import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Tablet, Headset, Users, MonitorSmartphone, CheckCircle2, Loader2, ShieldCheck, FileDown } from "lucide-react";
import { ShareWhatsApp } from "../ShareWhatsApp";
import { api, apiError } from "../../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Temoignages } from "../Temoignages";

export const fmt = (n) => n.toLocaleString("fr-FR").replace(/\u202f|\u00a0/g, " ");
export const ttc = (ht, tva = 0.18) => Math.round(ht * (1 + tva));

const BTN = "inline-flex items-center justify-center gap-2 rounded-full bg-[#0369A1] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#075985]";

function DemoForm({ offre, licences, labelStructure }) {
  const [f, setF] = useState({ establishment: "", responsable: "", email: new URLSearchParams(window.location.search).get("email") || "", telephone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/licenses/demo-request", { ...f, postes: licences, offre: `Vitalis ${offre}` });
      toast.success(data.message || "Demande envoyée");
      setDone(true);
    } catch (err) { toast.error(apiError(err, "Envoi impossible")); } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="offre-demo-success">
        <CheckCircle2 size={40} className="mx-auto text-emerald-600" />
        <p className="mt-3 font-display text-lg font-bold text-navy">Merci, votre demande est enregistrée</p>
        <p className="mt-1 text-sm text-slate-600">Un conseiller du Cabinet CARIS Santé vous contactera sous 48 h pour planifier la démonstration.</p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="offre-demo-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <div><Label htmlFor="of-structure">{labelStructure} *</Label><Input id="of-structure" required value={f.establishment} onChange={set("establishment")} data-testid="offre-demo-establishment" /></div>
        <div><Label htmlFor="of-nom">Votre nom *</Label><Input id="of-nom" required value={f.responsable} onChange={set("responsable")} placeholder="Prénom Nom" data-testid="offre-demo-responsable" /></div>
        <div><Label htmlFor="of-tel">Téléphone *</Label><Input id="of-tel" required value={f.telephone} onChange={set("telephone")} placeholder="+225 …" data-testid="offre-demo-telephone" /></div>
        <div><Label htmlFor="of-email">Email *</Label><Input id="of-email" type="email" required value={f.email} onChange={set("email")} data-testid="offre-demo-email" /></div>
      </div>
      <div><Label htmlFor="of-msg">Message (optionnel)</Label><Textarea id="of-msg" rows={3} value={f.message} onChange={set("message")} placeholder="Contexte, volume d'activité, créneaux souhaités…" data-testid="offre-demo-message" /></div>
      <Button type="submit" disabled={busy} data-testid="offre-demo-submit" className="h-11 w-full rounded-full bg-[#0369A1] text-sm font-semibold text-white hover:bg-[#075985]">
        {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <MonitorSmartphone size={16} className="mr-2" />} Demander une démo
      </Button>
      <p className="text-center text-[11px] text-slate-500">Sans engagement · Réponse sous 48 h ouvrées</p>
    </form>
  );
}

export function OffrePage({ offre }) {
  const prixTtc = ttc(offre.prixHt, offre.tva);
  useEffect(() => {
    if (window.location.hash === "#demo") setTimeout(() => document.getElementById("demo")?.scrollIntoView({ behavior: "smooth" }), 150);
    if (window.location.hash === "#video") setTimeout(() => document.getElementById("video")?.scrollIntoView({ behavior: "smooth" }), 150);
    else window.scrollTo(0, 0);
  }, [offre.slug]);
  const goForm = () => document.getElementById("demo")?.scrollIntoView({ behavior: "smooth" });
  const inclus = [
    { icon: Users, title: `${offre.licences} licences utilisateurs`, text: offre.licencesTexte },
    { icon: MonitorSmartphone, title: `${offre.appareils} appareils par utilisateur`, text: "Ordinateur, tablette et téléphone pour chaque compte." },
    { icon: Tablet, title: "Tablette de qualité incluse", text: "Livrée configurée, prête à l'emploi dès le premier jour." },
    { icon: Headset, title: "Support technique inclus", text: "Assistance par téléphone et messagerie, mises à jour comprises." },
  ];
  const bullets = [
    `${offre.licences} utilisateurs · ${offre.appareils} appareils max. par utilisateur`,
    `${offre.modules.length} modules : ${offre.modules.map((m) => m.title).join(", ")}`,
    "Tablette de qualité livrée configurée", "Support technique et mises à jour inclus", "Hébergement cloud sécurisé",
  ];

  return (
    <div data-testid={`offre-${offre.slug}-page`}>
      <section className="bg-[#E0F2FE]">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-[#7DC4E8] bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#0369A1]" data-testid="offre-badge">Solutions Vitalis · Niveau {offre.niveau}</span>
            <h1 className="mt-4 font-display text-4xl font-bold leading-tight text-[#0C4A6E] sm:text-5xl" data-testid="offre-title">Vitalis {offre.nom}</h1>
            <p className="mt-4 text-base text-slate-700 md:text-lg">{offre.accroche}</p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button onClick={goForm} data-testid="offre-hero-cta" className={BTN}><MonitorSmartphone size={16} /> Demander une démo</button>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/offres/${offre.slug}/fiche.pdf`} download data-testid="offre-fiche-pdf" className="inline-flex items-center gap-2 rounded-full border border-[#7DC4E8] bg-white px-5 py-3 text-sm font-semibold text-[#0369A1] transition-colors hover:bg-[#F0F9FF]"><FileDown size={16} /> Fiche PDF de l'offre</a>
              <span className="text-sm text-slate-600"><b className="text-[#0369A1]">{fmt(offre.prixHt)} FCFA HT</b> / mois</span>
            </div>
          </div>
          <img src={offre.image} alt={`Vitalis ${offre.nom}`} className="w-full rounded-2xl border border-[#7DC4E8] shadow-sm" data-testid="offre-hero-image" />
        </div>
      </section>

      {offre.video && (
        <section id="video" className="mx-auto max-w-5xl scroll-mt-24 px-4 pt-14 sm:px-6" data-testid="offre-video-section">
          <h2 className="font-display text-base font-bold uppercase tracking-wider text-[#0369A1] md:text-lg">Démonstration en vidéo</h2>
          {!offre.videoVoix && <p className="mt-2 text-sm text-slate-500" data-testid="offre-video-muette-note">Version sous-titrée — la voix de Dr ANON Barthélémy sera bientôt disponible.</p>}
          <video controls preload="metadata" poster={offre.videoPoster} src={offre.video} data-testid="offre-video" className="mt-6 w-full rounded-2xl border border-[#7DC4E8] bg-[#E0F2FE] shadow-sm" />
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
            <a href={offre.video} download data-testid="offre-video-download" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0369A1] hover:underline"><FileDown size={15} /> Télécharger la vidéo (MP4)</a>
            {offre.videoCourte && <a href={offre.videoCourte} download data-testid="offre-video-courte-download" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0369A1] hover:underline"><FileDown size={15} /> Version courte (WhatsApp)</a>}
            {offre.videoCourte && <ShareWhatsApp video={offre.videoCourte} titre={`Vitalis ${offre.nom}`} testid="offre-video-share" />}
          </div>
        </section>
      )}

      <section className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
        <h2 className="font-display text-base font-bold uppercase tracking-wider text-[#0369A1] md:text-lg">Modules inclus</h2>
        <div className={`mt-6 grid gap-5 sm:grid-cols-2 ${offre.modules.length >= 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"}`} data-testid="offre-modules">
          {offre.modules.map(({ icon: Icon, title, text }) => (
            <div key={title} className="rounded-2xl border border-[#7DC4E8] bg-white p-5 transition-transform duration-200 hover:-translate-y-0.5">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E0F2FE] text-[#0369A1]"><Icon size={22} /></div>
              <h3 className="mt-4 font-display text-base font-bold text-navy">{title}</h3>
              <p className="mt-2 text-sm text-slate-600">{text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 py-14 sm:px-6 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <h2 className="font-display text-base font-bold uppercase tracking-wider text-[#0369A1] md:text-lg">Tout est compris</h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="offre-inclus">
              {inclus.map(({ icon: Icon, title, text }) => (
                <div key={title} className="flex gap-3 rounded-xl border border-slate-200 bg-[#F8FBFE] p-4">
                  <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#0369A1] text-white"><Icon size={18} /></div>
                  <div><p className="text-sm font-semibold text-navy">{title}</p><p className="mt-1 text-xs text-slate-600">{text}</p></div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border-2 border-[#0369A1] bg-[#E0F2FE] p-6 lg:col-span-2" data-testid="offre-pricing">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-[#0369A1]">Abonnement mensuel</p>
            <p className="mt-2 font-display text-4xl font-bold text-[#0C4A6E]" data-testid="offre-price-ht">{fmt(offre.prixHt)} <span className="text-lg">FCFA HT</span></p>
            <p className="text-sm text-slate-600">par mois · TVA 18 % en sus</p>
            <p className="mt-3 rounded-lg bg-white px-3 py-2 text-sm text-slate-700" data-testid="offre-price-ttc">soit <b className="text-navy">{fmt(prixTtc)} FCFA TTC</b> / mois</p>
            <ul className="mt-4 space-y-2 text-sm text-slate-700">
              {bullets.map((t) => <li key={t} className="flex items-start gap-2"><ShieldCheck size={16} className="mt-0.5 shrink-0 text-[#0369A1]" />{t}</li>)}
            </ul>
            <button onClick={goForm} data-testid="offre-pricing-cta" className={`${BTN} mt-6 w-full`}>Demander une démo</button>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/offres/${offre.slug}/fiche.pdf`} download data-testid="offre-pricing-fiche-pdf" className="mt-3 inline-flex w-full items-center justify-center gap-2 text-sm font-semibold text-[#0369A1] hover:underline"><FileDown size={15} /> Télécharger la fiche PDF</a>
          </div>
        </div>
      </section>

      {offre.temoignages && <Temoignages items={offre.temoignages} sousTitre={offre.temoignagesSousTitre} />}

      <section id="demo" className="bg-[#E0F2FE]">
        <div className="mx-auto max-w-3xl px-4 py-14 sm:px-6">
          <h2 className="font-display text-2xl font-bold text-[#0C4A6E] sm:text-3xl">Voir Vitalis {offre.nom} en action</h2>
          <p className="mt-2 text-sm text-slate-700">{offre.demoTexte || "Une démonstration de 30 minutes, en visioconférence ou sur site, adaptée à votre organisation."}</p>
          <div className="mt-6 rounded-2xl border border-[#7DC4E8] bg-white p-6 shadow-sm"><DemoForm offre={offre.nom} licences={offre.licences} labelStructure={offre.labelStructure || "Nom de la structure"} /></div>
        </div>
      </section>
    </div>
  );
}
